import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import {
  filterSentryEvents,
  httpClientIntegration,
  initLogging,
  logError,
  thirdPartyErrorFilterIntegration,
} from '@bonanza-social/toolkit/logging';
import {
  ApiError,
  ValidationError,
  initApi,
} from '@bonanza-social/toolkit/api';
import { Config } from '@bonanza-social/toolkit/config';
import { SecureStorage } from '@bonanza-social/toolkit/storage';
import {
  refreshToken,
  getRefreshTokenPromise,
  logout,
} from '@bonanza-social/auth/state';
import { getDeviceFingerprint, getDeviceId, getSessionId } from './src/utils';
import packageJson from './package.json';

import './src/assets';
export * from './src/assets';
import App from './src/App';

import '@bonanza-social/shared/state';

import '@bonanza-social/auth/state';
import '@bonanza-social/account/state';
import '@bonanza-social/account/navigation';
import '@bonanza-social/games/state';
import '@bonanza-social/wallet/state';
import '@bonanza-social/rewards/state';
import '@bonanza-social/geo-fence';
import '@bonanza-social/status/state';
import '@bonanza-social/levels/state';
import '@bonanza-social/chat/state';
import '@bonanza-social/smartico/state';
import '@bonanza-social/transmit-resolver';
import '@bonanza-social/onboarding';
import '@bonanza-social/inbox';
import '@bonanza-social/shop';
import '@bonanza-social/prize-wall';
import '@bonanza-social/cashback/state';
import '@bonanza-social/daily-rewards/state';
import '@bonanza-social/meta-games/state';

import './src/state';

import { store } from '@bonanza-social/toolkit/store';
import { isRejectedWithValue } from '@bonanza-social/toolkit/state';
import { isPaymentResultIframe } from '@bonanza-social/toolkit/utils';
import { Toast } from '@bonanza-social/moonlight';

if (isPaymentResultIframe()) {
  window.top?.postMessage(
    JSON.stringify({
      type: 'PAYMENT_RESULT',
      location: `${window.location.pathname}${window.location.search}`,
    }),
  );
} else {
  if (Config.SENTRY_DSN) {
    initLogging({
      enabled: !__DEV__,
      dsn: Config.SENTRY_DSN,
      environment: Config.ENVIRONMENT,
      release: packageJson.version,
      dist: 'web',
      maxValueLength: 1024,
      ignoreErrors: [
        'TypeError: Failed to fetch',
        'TypeError: Load failed',
        'Load failed',
        'TypeError: NetworkError when attempting to fetch resource.',
        '/Failed to start the audio device/i',
        // Ignore Firebase IndexedDB noise
        // Browser or OS event for clearing Firebase SDK stored cached data
        '/Database deleted by request of the user/i',
      ],
      beforeSend: filterSentryEvents,
      integrations: [httpClientIntegration, thirdPartyErrorFilterIntegration],
    });
  }

  initApi({
    getBaseUrl: () => Config.API_URL,
    getSocketUrl: () => `${Config.API_URL}/notifications/hubs/generic`,
    getUserId: async () => {
      const id = await SecureStorage.getItem('user_id');

      return id;
    },
    getAccessToken: async () => {
      const token = await SecureStorage.getItem('access_token');

      return token;
    },
    refreshToken: async () => {
      const result = await store.dispatch(refreshToken());

      if (refreshToken.fulfilled.match(result)) {
        return result.payload.access_token;
      }

      if (isRejectedWithValue(result) && result.payload?.status) {
        throw new ApiError('Unable to refresh token', result.payload);
      }

      throw new Error('Unable to refresh token unknown error');
    },
    waitForRefreshToken: async (url) => {
      const refreshTokenPromise = getRefreshTokenPromise();

      if (refreshTokenPromise && url.indexOf('/connect/token') === -1) {
        await refreshTokenPromise;
      }
    },
    getToken: async () => {
      const tokenString = await SecureStorage.getItem('token');

      if (tokenString) {
        const token = JSON.parse(tokenString);

        return token;
      }
    },
    getRequestHeaders: async () => {
      const deviceId = await getDeviceId();
      const sessionId = getSessionId();
      const deviceFingerprint = getDeviceFingerprint();

      return {
        'x-device-metadata': deviceFingerprint,
        'x-fe-session-id': sessionId,
        'x-fe-device-id': deviceId,
        'x-channel-name': 'web',
      };
    },
    onApiError: (error) => {
      if (error instanceof ApiError && error.status >= 500) {
        if (
          error.status === 503 &&
          error.data?.errorCode === 'system-under-maintenance'
        ) {
          store.dispatch(logout(false));

          Toast.show({
            type: 'error',
            title: error.data?.title,
            text: error.data.message,
          });
        }

        logError('API 500 Error', error);
      } else if (error instanceof ValidationError) {
        logError('Response Validation Error', error.data);
      }
    },
  });

  const container =
    document.getElementById('bonanza-social-root') || document.body;
  const root = createRoot(container);

  root.render(createElement(App));

  const spinner = document.getElementById('spinner');

  if (spinner) {
    document.body.removeChild(spinner);
  }
}
